import { useState, useEffect } from 'react';
import {
  CreditCard,
  Banknote,
  Gift,
  Loader2,
  DollarSign,
  CheckCircle2,
  Users,
  Plus,
  Minus,
  Trash2,
  Split,
  UserPlus,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';

type PaymentMethod = 'cash' | 'card' | 'gift_card' | 'other';
type SplitMethod = 'equal' | 'custom';

interface Order {
  id: string;
  order_number: string;
  total: number;
  status: string;
  items?: Array<{
    id: string;
    name: string;
    quantity: number;
    total: number;
    seat_number?: number;
  }>;
}

interface SplitPayment {
  id: string;
  label: string;
  amount: number;
  tipAmount: number;
  paymentMethod: PaymentMethod;
  isPaid: boolean;
}

interface SplitBillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  onProcessPayments: (
    orderId: string,
    payments: Array<{
      paymentMethod: PaymentMethod;
      amount: number;
      tipAmount?: number;
    }>
  ) => Promise<void>;
  isProcessing?: boolean;
}

const paymentMethods: Array<{
  id: PaymentMethod;
  label: string;
  icon: typeof CreditCard;
}> = [
  { id: 'cash', label: 'Cash', icon: Banknote },
  { id: 'card', label: 'Card', icon: CreditCard },
  { id: 'gift_card', label: 'Gift', icon: Gift },
  { id: 'other', label: 'Other', icon: DollarSign },
];

export function SplitBillDialog({
  open,
  onOpenChange,
  order,
  onProcessPayments,
  isProcessing = false,
}: SplitBillDialogProps) {
  const { formatCurrency } = useCurrency();
  const [splitMethod, setSplitMethod] = useState<SplitMethod>('equal');
  const [numberOfSplits, setNumberOfSplits] = useState(2);
  const [splits, setSplits] = useState<SplitPayment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [processingIndex, setProcessingIndex] = useState<number | null>(null);

  // Initialize splits when order changes or dialog opens
  useEffect(() => {
    if (open && order) {
      initializeSplits();
    }
  }, [open, order, numberOfSplits, splitMethod]);

  const initializeSplits = () => {
    if (!order) return;

    const equalAmount = Math.floor((order.total / numberOfSplits) * 100) / 100;
    const remainder = Math.round((order.total - equalAmount * numberOfSplits) * 100) / 100;

    const newSplits: SplitPayment[] = [];
    for (let i = 0; i < numberOfSplits; i++) {
      // Add remainder to the last split to account for rounding
      const amount = i === numberOfSplits - 1 ? equalAmount + remainder : equalAmount;
      newSplits.push({
        id: `split-${i}`,
        label: `Guest ${i + 1}`,
        amount,
        tipAmount: 0,
        paymentMethod: 'cash',
        isPaid: false,
      });
    }
    setSplits(newSplits);
    setError(null);
  };

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setSplitMethod('equal');
      setNumberOfSplits(2);
      setError(null);
      setProcessingIndex(null);
    }
    onOpenChange(isOpen);
  };

  const updateSplit = (index: number, updates: Partial<SplitPayment>) => {
    setSplits((prev) =>
      prev.map((split, i) => (i === index ? { ...split, ...updates } : split))
    );
  };

  const addSplit = () => {
    if (!order) return;

    // Recalculate amounts to include new split
    const newCount = splits.length + 1;
    const equalAmount = Math.floor((order.total / newCount) * 100) / 100;
    const remainder = Math.round((order.total - equalAmount * newCount) * 100) / 100;

    const newSplits = splits.map((split) => ({
      ...split,
      amount: split.isPaid ? split.amount : equalAmount,
    }));

    // Adjust last unpaid split for remainder
    const lastUnpaidIndex = newSplits.findLastIndex((s: SplitPayment) => !s.isPaid);
    if (lastUnpaidIndex >= 0) {
      newSplits[lastUnpaidIndex].amount += remainder;
    }

    newSplits.push({
      id: `split-${newCount}`,
      label: `Guest ${newCount}`,
      amount: equalAmount,
      tipAmount: 0,
      paymentMethod: 'cash',
      isPaid: false,
    });

    setSplits(newSplits);
    setNumberOfSplits(newCount);
  };

  const removeSplit = (index: number) => {
    if (splits.length <= 2 || splits[index].isPaid) return;

    const removedAmount = splits[index].amount;
    const newSplits = splits.filter((_, i) => i !== index);

    // Redistribute the removed amount to remaining unpaid splits
    const unpaidSplits = newSplits.filter((s) => !s.isPaid);
    if (unpaidSplits.length > 0) {
      const additionalEach = removedAmount / unpaidSplits.length;
      newSplits.forEach((split) => {
        if (!split.isPaid) {
          split.amount = Math.round((split.amount + additionalEach) * 100) / 100;
        }
      });
    }

    setSplits(newSplits);
    setNumberOfSplits(newSplits.length);
  };

  const handlePaySplit = async (index: number) => {
    if (!order) return;

    const split = splits[index];
    if (split.isPaid || split.amount <= 0) return;

    setError(null);
    setProcessingIndex(index);

    try {
      await onProcessPayments(order.id, [
        {
          paymentMethod: split.paymentMethod,
          amount: split.amount,
          tipAmount: split.tipAmount > 0 ? split.tipAmount : undefined,
        },
      ]);

      updateSplit(index, { isPaid: true });
    } catch (err: any) {
      setError(err.message || 'Failed to process payment');
    } finally {
      setProcessingIndex(null);
    }
  };

  const handlePayAll = async () => {
    if (!order) return;

    const unpaidSplits = splits.filter((s) => !s.isPaid && s.amount > 0);
    if (unpaidSplits.length === 0) {
      handleOpen(false);
      return;
    }

    setError(null);
    setProcessingIndex(-1); // -1 indicates processing all

    try {
      await onProcessPayments(
        order.id,
        unpaidSplits.map((split) => ({
          paymentMethod: split.paymentMethod,
          amount: split.amount,
          tipAmount: split.tipAmount > 0 ? split.tipAmount : undefined,
        }))
      );

      setSplits((prev) => prev.map((s) => ({ ...s, isPaid: true })));
      handleOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to process payments');
    } finally {
      setProcessingIndex(null);
    }
  };

  if (!order) return null;

  const totalAssigned = splits.reduce((sum, s) => sum + s.amount, 0);
  const totalPaid = splits.filter((s) => s.isPaid).reduce((sum, s) => sum + s.amount, 0);
  const remainingBalance = order.total - totalPaid;
  const allPaid = splits.every((s) => s.isPaid);
  const hasUnbalanced = Math.abs(totalAssigned - order.total) > 0.01;

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpen}>
      <ResponsiveModalContent maxWidth="lg">
        <ResponsiveModalHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Split className="h-5 w-5 text-primary" />
            </div>
            <div>
              <ResponsiveModalTitle>Split Bill</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Order #{order.order_number} - Total: {formatCurrency(order.total)}
              </ResponsiveModalDescription>
            </div>
          </div>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-6">
          {/* Split Method Tabs */}
          <Tabs value={splitMethod} onValueChange={(v) => setSplitMethod(v as SplitMethod)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="equal" className="gap-2">
                <Users className="h-4 w-4" />
                Equal Split
              </TabsTrigger>
              <TabsTrigger value="custom" className="gap-2">
                <DollarSign className="h-4 w-4" />
                Custom Amounts
              </TabsTrigger>
            </TabsList>

            <TabsContent value="equal" className="mt-4">
              <div className="flex items-center justify-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (numberOfSplits > 2) {
                      setNumberOfSplits(numberOfSplits - 1);
                    }
                  }}
                  disabled={numberOfSplits <= 2}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="text-center">
                  <div className="text-3xl font-bold">{numberOfSplits}</div>
                  <div className="text-sm text-muted-foreground">
                    {formatCurrency(order.total / numberOfSplits)} each
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (numberOfSplits < 20) {
                      setNumberOfSplits(numberOfSplits + 1);
                    }
                  }}
                  disabled={numberOfSplits >= 20}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="custom" className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">
                Adjust individual amounts below. Use the + button to add more splits.
              </p>
            </TabsContent>
          </Tabs>

          {/* Split Cards */}
          <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
            {splits.map((split, index) => (
              <div
                key={split.id}
                className={cn(
                  'p-4 rounded-lg border transition-colors',
                  split.isPaid
                    ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900'
                    : 'bg-card border-border'
                )}
              >
                <div className="flex items-start gap-4">
                  {/* Label and Amount */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {splitMethod === 'custom' ? (
                          <Input
                            value={split.label}
                            onChange={(e) =>
                              updateSplit(index, { label: e.target.value })
                            }
                            className="h-7 w-28 text-sm"
                            disabled={split.isPaid}
                          />
                        ) : (
                          <span className="font-medium">{split.label}</span>
                        )}
                        {split.isPaid && (
                          <Badge
                            variant="secondary"
                            className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Paid
                          </Badge>
                        )}
                      </div>
                      {!split.isPaid && splits.length > 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => removeSplit(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {/* Amount Input (for custom mode) */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={split.amount.toFixed(2)}
                            onChange={(e) =>
                              updateSplit(index, {
                                amount: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="pl-6 h-8 text-sm"
                            disabled={split.isPaid || splitMethod === 'equal'}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Tip</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={split.tipAmount.toFixed(2)}
                            onChange={(e) =>
                              updateSplit(index, {
                                tipAmount: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="pl-6 h-8 text-sm"
                            disabled={split.isPaid}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Payment Method */}
                    {!split.isPaid && (
                      <div className="flex gap-1">
                        {paymentMethods.map((method) => {
                          const Icon = method.icon;
                          const isSelected = split.paymentMethod === method.id;
                          return (
                            <Button
                              key={method.id}
                              variant={isSelected ? 'default' : 'outline'}
                              size="sm"
                              className="flex-1 gap-1 h-7 text-xs"
                              onClick={() =>
                                updateSplit(index, { paymentMethod: method.id })
                              }
                            >
                              <Icon className="h-3 w-3" />
                              {method.label}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Pay Button */}
                  {!split.isPaid && (
                    <Button
                      variant="blaze"
                      className="shrink-0"
                      onClick={() => handlePaySplit(index)}
                      disabled={
                        processingIndex !== null ||
                        split.amount <= 0 ||
                        isProcessing
                      }
                    >
                      {processingIndex === index ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>Pay {formatCurrency(split.amount + split.tipAmount)}</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {/* Add Guest Button */}
            {splitMethod === 'custom' && (
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={addSplit}
                disabled={splits.length >= 20}
              >
                <UserPlus className="h-4 w-4" />
                Add Guest
              </Button>
            )}
          </div>

          {/* Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order Total</span>
              <span>{formatCurrency(order.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Assigned</span>
              <span className={cn(hasUnbalanced && 'text-amber-600')}>
                {formatCurrency(totalAssigned)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Paid</span>
              <span className="text-green-600">{formatCurrency(totalPaid)}</span>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t">
              <span>Remaining Balance</span>
              <span className={cn(remainingBalance > 0 && 'text-amber-600')}>
                {formatCurrency(remainingBalance)}
              </span>
            </div>
            {hasUnbalanced && (
              <p className="text-xs text-amber-600">
                Warning: Assigned amounts do not match order total.
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
              {error}
            </p>
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button
            variant="outline"
            onClick={() => handleOpen(false)}
            disabled={processingIndex !== null}
          >
            {allPaid ? 'Close' : 'Cancel'}
          </Button>
          {!allPaid && (
            <Button
              variant="blaze"
              onClick={handlePayAll}
              disabled={
                processingIndex !== null ||
                splits.every((s) => s.isPaid || s.amount <= 0) ||
                isProcessing
              }
              className="gap-2"
            >
              {processingIndex === -1 ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Pay All Remaining ({formatCurrency(remainingBalance)})
                </>
              )}
            </Button>
          )}
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
